
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Tab, Tabs } from 'react-bootstrap';
import { organizationStore } from '../../store/OrganizationStore';
import { useSnapshot } from 'valtio';
import OrganizationForm from './organizationForm';
import OrgPickerList from './components/OrgPickerList';
import { getDescendantIds } from './components/orgHierarchy';
import useOrganization from './hooks/useOrganization';
import { Organization } from '@models/organization.model.ts';
import { OrganizationService } from '@services/organizations/OrganizationService.ts';
import showAlert from '@/shared/utils/generic';

export default function CreateOrganization(_props: any) {
	useSnapshot(organizationStore);
	const { create, organizationForm, setOrganizationForm } = useOrganization();
	const { showCreateModal, pendingParentId, organizationList } = organizationStore;

	const [activeTab, setActiveTab] = useState<string>('new');
	const [existingOrgId, setExistingOrgId] = useState<number | null>(null);
	const [moving, setMoving] = useState(false);

	useEffect(() => {
		if (showCreateModal) {
			const fresh = new Organization();
			fresh.parent_id = pendingParentId;
			setOrganizationForm(fresh);
			setActiveTab('new');
			setExistingOrgId(null);
		}
	}, [showCreateModal, pendingParentId]);

	const parentOrg = useMemo(
		() => (pendingParentId != null ? organizationList.find((o) => o.id === pendingParentId) : null),
		[pendingParentId, organizationList]
	);

	const excludeIds = useMemo(() => {
		if (!parentOrg) return new Set<number>();
		const ids = getDescendantIds(parentOrg.id, organizationList);
		ids.add(parentOrg.id);
		return ids;
	}, [parentOrg, organizationList]);

	const handleMoveExisting = async () => {
		if (existingOrgId == null || !parentOrg) return;
		setMoving(true);
		try {
			await new OrganizationService().setOrganizationParent(existingOrgId, parentOrg.id);
			organizationStore.setModalTemplateVisibility(false);
			showAlert('Organization moved', 'success', 2500);
		} catch (e: any) {
			showAlert(e?.message ?? 'Failed to move organization', 'error', 4000);
		} finally {
			setMoving(false);
		}
	};

	const selectedExistingOrg = useMemo(
		() => (existingOrgId != null ? organizationList.find((o) => o.id === existingOrgId) : null),
		[existingOrgId, organizationList]
	);

	return (
		<Modal id="e1" style={{background: "rgba(0,0,0,.5)"}}
            className="fade" aria-hidden="true"
            aria-labelledby="contained-modal-title-vcenter"
            show={showCreateModal}
            size="lg"
            onHide={()=>{organizationStore.setModalTemplateVisibility(false)}}
        >
			<Modal.Header closeButton>
				<Modal.Title as="h5">
					{parentOrg ? "Add sub-organization" : "Create Organization"}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{parentOrg && (
					<div className="alert alert-light border d-flex align-items-center gap-2 mb-3 py-2 px-3">
						<i className="bi bi-diagram-3 text-primary"></i>
						<span className="text-muted">Adding under</span>
						<span className="fw-semibold">{parentOrg.name}</span>
					</div>
				)}

				{parentOrg ? (
					<Tabs
						activeKey={activeTab}
						onSelect={(k) => setActiveTab(k || 'new')}
						className="mb-3"
					>
						<Tab eventKey="new" title="Create new">
							<OrganizationForm
								updateData={create}
								organizationForm={organizationForm}
								updateForm={setOrganizationForm}
							/>
						</Tab>
						<Tab eventKey="existing" title="Use existing">
							<OrgPickerList
								allOrgs={organizationList}
								excludeIds={excludeIds}
								selectedId={existingOrgId}
								onSelect={setExistingOrgId}
								placeholder="Search for an existing organization…"
							/>
							<div className="d-flex justify-content-end mt-3">
								<Button
									variant="primary"
									onClick={handleMoveExisting}
									disabled={existingOrgId == null || moving}
								>
									{moving
										? 'Moving…'
										: selectedExistingOrg
										? `Move "${selectedExistingOrg.name}" under ${parentOrg.name}`
										: `Move under ${parentOrg.name}`}
								</Button>
							</div>
						</Tab>
					</Tabs>
				) : (
					<OrganizationForm
						updateData={create}
						organizationForm={organizationForm}
						updateForm={setOrganizationForm}
					/>
				)}
			</Modal.Body>
		</Modal>
	);

}
